// frontend/src/components/ManagerApprovalAttendance.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Badge,
  Tabs,
  Tab,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Avatar
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  AccessTime,
  Person,
  EventNote,
  Message,
  Notifications,
  History,
  Description,
  Send,
  Refresh
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  transition: 'transform 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4]
  }
}));

function ManagerApprovalAttendance() {
  const [pendingRequests, setPendingRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState(''); // 'approve' or 'reject'
  const [comments, setComments] = useState('');
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [employeeId, setEmployeeId] = useState(null);
  const [error, setError] = useState(null);
  const [companyConfig, setCompanyConfig] = useState({ dateFormat: 'DD/MM/YYYY', timezone: 'Asia/Kolkata' });
  const getManagerIdFromToken = async () => {
     try {
       const token = localStorage.getItem('token');
       if (!token) {
         alert("No authentication token found. Please log in.");
         return;
       }
       
       const payload = JSON.parse(atob(token.split('.')[1]));
       const company_id = payload.company_id;
       
       // Try to get from user API
       try {
         const response = await axios.get(`${API_URL}/api/user/current?company_id=${company_id}`, {
           withCredentials: true
         });
         
         if (response.data && response.data.employee_id) {
           setEmployeeId(response.data.employee_id);

           try {
             const cmpRes = await axios.get(`${API_URL}/api/cmp/${company_id}`, { headers: { Authorization: `Bearer ${token}` } });
             if (cmpRes.data && cmpRes.data.data) {
               setCompanyConfig({
                 dateFormat: cmpRes.data.data.dateFormat || 'DD/MM/YYYY',
                 timezone: cmpRes.data.data.timezone || 'Asia/Kolkata'
               });
             }
           } catch(e) {}

           return response.data.employee_id;
         }
       } catch (err) {
       }
       
       // Fallback to payload
       if (payload.employee_id) {
         setEmployeeId(payload.employee_id);
         return payload.employee_id;
       }
       
       throw new Error('Employee ID not found');
     } catch (err) {
       //console.error("Error fetching current user:", err);
       setError('Failed to fetch user information');
       return null;
     }
   };


  const getCompanyIdFromToken = () => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.company_id;
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    fetchPendingRequests();
    fetchNotifications();
    
    // Poll for new requests every 30 seconds
    const interval = setInterval(() => {
      fetchPendingRequests();
      fetchNotifications();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchPendingRequests = async () => {
    try {
      const manager_id = await getManagerIdFromToken();
      const company_id = getCompanyIdFromToken();
      const token = localStorage.getItem('token');
      if (!manager_id || !company_id) return;
      
      const response = await axios.get(
        `${API_URL}/api/attendance-requests/pending/${manager_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          
          }
        }
      );
      
      if (response.data.success) {
        setPendingRequests(response.data.data);
      }
    } catch (error) {
      //console.error('Error fetching pending requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const manager_id = await getManagerIdFromToken();
      const company_id = getCompanyIdFromToken();
      const token = localStorage.getItem('token');
      if (!manager_id || !company_id) return;
      
      const response = await axios.get(
        `${API_URL}/api/attendance-requests/notifications/${manager_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
           
          }
        }
      );
      
      if (response.data.success) {
        setNotifications(response.data.data.notifications);
        setUnreadCount(response.data.data.unreadCount);
      }
    } catch (error) {
      //console.error('Error fetching notifications:', error);
    }
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;
    
    try {
      const manager_id = await getManagerIdFromToken();
      const company_id = getCompanyIdFromToken();
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/attendance-requests/${selectedRequest.id}/approve?approved_by=${manager_id}`,
        { manager_comments: comments },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      
      showNotification('Request approved successfully!', 'success');
      setOpenDialog(false);
      setComments('');
      fetchPendingRequests();
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to approve request', 'error');
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;
    
    if (!comments) {
      showNotification('Please provide a reason for rejection', 'warning');
      return;
    }
    
    try {
      const manager_id = await getManagerIdFromToken();
      const company_id = getCompanyIdFromToken();
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/attendance-requests/${selectedRequest.id}/reject?rejected_by=${manager_id}`,
        { manager_comments: comments },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      
      showNotification('Request rejected successfully', 'success');
      setOpenDialog(false);
      setComments('');
      fetchPendingRequests();
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to reject request', 'error');
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      const manager_id = await getManagerIdFromToken();
      const company_id = getCompanyIdFromToken();
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/attendance-requests/notifications/${notificationId}/read?user_id=${manager_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      
      fetchNotifications();
    } catch (error) {
      //console.error('Error marking notification read:', error);
    }
  };

  const showNotification = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const formatTime = (time) => {
    if (!time) return '-';
    return time;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4"  fontWeight="bold">
          Attendance Regularization 
        </Typography>
        {/* <Badge badgeContent={unreadCount} color="error">
          <IconButton onClick={fetchNotifications}>
            <Notifications />
          </IconButton>
        </Badge> */}
      </Box>

     

      {/* Tabs */}
      {/* <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Pending Requests" icon={<Description />} />
          <Tab label="Notifications" icon={<Badge badgeContent={unreadCount} color="error"><Notifications /></Badge>} />
        </Tabs>
      </Paper> */}

      {/* Pending Requests Tab */}
      {tabValue === 0 && (
        <>
          {pendingRequests.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 64, color: '#4caf50', mb: 2 }} />
              <Typography variant="h6">No pending requests</Typography>
              <Typography color="textSecondary">All attendance requests have been processed.</Typography>
            </Paper>
          ) : (
            pendingRequests.map((request) => (
              <StyledCard key={request.id}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Person color="primary" />
                        <Typography variant="subtitle1" fontWeight="bold">
                          {request.employee?.firstName} {request.employee?.lastName}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <EventNote color="action" />
                        <Typography variant="body2" color="textSecondary">
                          {dayjs(request.date).tz(companyConfig.timezone).format(companyConfig.dateFormat)}
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <Box mb={1}>
                        <Typography variant="caption" color="textSecondary">Original Times</Typography>
                        <Box display="flex" gap={1}>
                          <Chip size="small" label={`In: ${formatTime(request.original_in_time)}`} />
                          <Chip size="small" label={`Out: ${formatTime(request.original_out_time)}`} />
                        </Box>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">Requested Times</Typography>
                        <Box display="flex" gap={1}>
                          <Chip size="small" color="primary" label={`In: ${formatTime(request.requested_in_time)}`} />
                          <Chip size="small" color="primary" label={`Out: ${formatTime(request.requested_out_time)}`} />
                        </Box>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <Box display="flex" gap={1} justifyContent="flex-end">
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<CheckCircle />}
                          onClick={() => {
                            setSelectedRequest(request);
                            setDialogType('approve');
                            setOpenDialog(true);
                          }}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<Cancel />}
                          onClick={() => {
                            setSelectedRequest(request);
                            setDialogType('reject');
                            setOpenDialog(true);
                          }}
                        >
                          Reject
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                  
                  {request.reason && (
                    <Box mt={2} p={2} bgcolor="#f5f5f5" borderRadius={2}>
                      <Typography variant="caption" color="textSecondary">
                        <Message fontSize="small" /> Employee Reason:
                      </Typography>
                      <Typography variant="body2">{request.reason}</Typography>
                    </Box>
                  )}
                </CardContent>
              </StyledCard>
            ))
          )}
        </>
      )}

      {/* Notifications Tab */}
      {tabValue === 1 && (
        <>
          {notifications.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Notifications sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
              <Typography variant="h6">No notifications</Typography>
            </Paper>
          ) : (
            notifications.map((notification) => (
              <StyledCard 
                key={notification.id} 
                sx={{ bgcolor: notification.is_read ? '#fff' : '#f3e5f5', cursor: 'pointer' }}
                onClick={() => !notification.is_read && markNotificationRead(notification.id)}
              >
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {notification.title}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" mt={1}>
                        {notification.message}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" mt={1} display="block">
                        {dayjs(notification.createdAt).tz(companyConfig.timezone).format(`${companyConfig.dateFormat} hh:mm A`)}
                      </Typography>
                    </Box>
                    {!notification.is_read && (
                      <Chip label="New" size="small" color="primary" />
                    )}
                  </Box>
                </CardContent>
              </StyledCard>
            ))
          )}
        </>
      )}

      {/* Approval/Rejection Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogType === 'approve' ? 'Approve Request' : 'Reject Request'}
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Employee: {selectedRequest.employee?.firstName} {selectedRequest.employee?.lastName}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Date: {dayjs(selectedRequest.date).tz(companyConfig.timezone).format(companyConfig.dateFormat)}
              </Typography>
              <Box sx={{ my: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="caption" color="textSecondary">Requested Changes:</Typography>
                <Box display="flex" gap={2} mt={1}>
                  <Typography variant="body2">
                    <strong>In:</strong> {formatTime(selectedRequest.original_in_time)} → {formatTime(selectedRequest.requested_in_time)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Out:</strong> {formatTime(selectedRequest.original_out_time)} → {formatTime(selectedRequest.requested_out_time)}
                  </Typography>
                </Box>
              </Box>
              <TextField
                fullWidth
                label={dialogType === 'approve' ? 'Comments (Optional)' : 'Reason for Rejection (Required)'}
                multiline
                rows={3}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                required={dialogType === 'reject'}
                error={dialogType === 'reject' && !comments}
                helperText={dialogType === 'reject' && !comments ? 'Please provide a reason' : ''}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color={dialogType === 'approve' ? 'success' : 'error'}
            onClick={dialogType === 'approve' ? handleApprove : handleReject}
          >
            {dialogType === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default ManagerApprovalAttendance;